/**
 * Factories API Client
 * Strapi integration for factory CRUD operations
 */

import { getHttpClient, i18n } from '@spcc/core';
import type {
  Factory,
  FactoryListItem,
  FactoryStats,
  FactoriesFilter,
  CreateFactoryRequest,
  UpdateFactoryRequest,
  PaginatedFactories,
  PaginationMeta,
  City,
  Country,
  Location,
} from '../../domain/types';
import { DEFAULT_PAGE_SIZE } from '../../domain/constants';
import {
  STRAPI_ENDPOINTS,
  buildFactoryQuery,
  type FactoryListResponse,
  type FactoryResponse,
  type CityDto,
  type CountryDto,
  type LocationDto,
} from '../../api/api-types';
import {
  transformFactoryDtoToFactory,
  transformFactoryDtoToListItem,
  transformCreateRequestToDto,
  transformUpdateRequestToDto,
  transformCityDtoToCity,
  transformCountryDtoToCountry,
  transformLocationDtoToLocation,
} from '../../api/transformers';

// ============================================================================
// Helper Functions
// ============================================================================

function buildStrapiQueryString(
  params: Record<string, string | boolean>
): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

function calculateFactoryStats(factories: FactoryListItem[]): FactoryStats {
  return {
    total: factories.length,
    active: factories.filter((f) => f.status === 'active').length,
    inactive: factories.filter((f) => f.status === 'inactive').length,
  };
}

function resolveFactoriesLanguage(): string {
  return (i18n.language || 'en').split('-')[0];
}

type FactoryArEntry = { name?: string };
type LocationArEntry = {
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
};

async function fetchFactoryArNamesMap(
  http: ReturnType<typeof getHttpClient>
): Promise<Map<string, FactoryArEntry>> {
  const map = new Map<string, FactoryArEntry>();
  try {
    const qs = buildStrapiQueryString({
      locale: 'ar',
      'fields[0]': 'documentId',
      'fields[1]': 'name',
      'pagination[pageSize]': '500',
    });
    const resp = await http.get<{
      data: Array<{ documentId: string; name?: string }>;
    }>(`${STRAPI_ENDPOINTS.FACTORIES}${qs}`);
    for (const item of resp.data.data) {
      if (item.documentId)
        map.set(item.documentId, { name: item.name || undefined });
    }
  } catch {
    /* AR entries are optional */
  }
  return map;
}

function mergeArIntoFactoryListItem(
  item: FactoryListItem,
  arMap: Map<string, FactoryArEntry>,
  appLang: string
): FactoryListItem {
  const ar = arMap.get(item.documentId);
  if (!ar?.name || appLang !== 'ar') return item;
  return { ...item, name: ar.name };
}

/**
 * Fetch AR translations for a list of city documentIds in a single request.
 * Returns a documentId -> {name, addressLine1?} map. Used as an "AR shim"
 * because Strapi v5 doesn't reliably surface the i18n `localizations`
 * sub-array on populated many-to-many sub-relations, and we can't request the
 * factory in `?locale=ar` without dropping cities that have no AR version.
 */
async function fetchCityArNamesMap(
  http: ReturnType<typeof getHttpClient>,
  documentIds: string[]
): Promise<Map<string, { name?: string }>> {
  const map = new Map<string, { name?: string }>();
  const ids = Array.from(new Set(documentIds.filter(Boolean)));
  if (ids.length === 0) return map;
  try {
    const params: Record<string, string | boolean> = {
      locale: 'ar',
      'fields[0]': 'documentId',
      'fields[1]': 'name',
      'pagination[pageSize]': String(Math.max(ids.length, 100)),
    };
    ids.forEach((id, i) => {
      params[`filters[documentId][$in][${i}]`] = id;
    });
    const qs = buildStrapiQueryString(params);
    const resp = await http.get<{
      data: Array<{ documentId: string; name?: string }>;
    }>(`/api/cities${qs}`);
    for (const item of resp.data.data ?? []) {
      if (item.documentId)
        map.set(item.documentId, { name: item.name || undefined });
    }
  } catch {
    /* AR entries are optional */
  }
  return map;
}

async function fetchLocationArEntriesMap(
  http: ReturnType<typeof getHttpClient>,
  documentIds: string[]
): Promise<Map<string, LocationArEntry>> {
  const map = new Map<string, LocationArEntry>();
  const ids = Array.from(new Set(documentIds.filter(Boolean)));
  if (ids.length === 0) return map;

  try {
    const params: Record<string, string | boolean> = {
      locale: 'ar',
      'fields[0]': 'documentId',
      'fields[1]': 'name',
      'fields[2]': 'addressLine1',
      'fields[3]': 'addressLine2',
      'pagination[pageSize]': String(Math.max(ids.length, 100)),
    };
    ids.forEach((id, index) => {
      params[`filters[documentId][$in][${index}]`] = id;
    });
    const qs = buildStrapiQueryString(params);
    const resp = await http.get<{
      data: Array<{
        documentId: string;
        name?: string;
        addressLine1?: string;
        addressLine2?: string;
      }>;
    }>(`${STRAPI_ENDPOINTS.LOCATIONS}${qs}`);

    for (const item of resp.data.data ?? []) {
      if (item.documentId) {
        map.set(item.documentId, {
          name: item.name || undefined,
          addressLine1: item.addressLine1 || undefined,
          addressLine2: item.addressLine2 || undefined,
        });
      }
    }
  } catch {
    // Arabic relation entries are optional.
  }

  return map;
}

async function patchCityDtosWithArNames(
  http: ReturnType<typeof getHttpClient>,
  dtos: CityDto[],
  lang: string
): Promise<void> {
  if (lang !== 'ar' || !Array.isArray(dtos) || dtos.length === 0) return;

  const cityArMap = await fetchCityArNamesMap(
    http,
    dtos
      .map((dto) => dto.documentId)
      .filter((documentId): documentId is string => Boolean(documentId))
  );

  if (cityArMap.size === 0) return;

  dtos.forEach((dto) => {
    const ar = cityArMap.get(dto.documentId);
    if (ar?.name) {
      dto.name = ar.name;
    }
  });
}

async function patchLocationDtosWithArNames(
  http: ReturnType<typeof getHttpClient>,
  dtos: LocationDto[],
  lang: string
): Promise<void> {
  if (lang !== 'ar' || !Array.isArray(dtos) || dtos.length === 0) return;

  const locationIds = dtos
    .map((dto) => dto.documentId)
    .filter((documentId): documentId is string => Boolean(documentId));
  const cityIds = dtos
    .map((dto) => dto.city?.documentId)
    .filter((documentId): documentId is string => Boolean(documentId));

  const [locationArMap, cityArMap] = await Promise.all([
    fetchLocationArEntriesMap(http, locationIds),
    fetchCityArNamesMap(http, cityIds),
  ]);

  dtos.forEach((dto) => {
    const locationAr = locationArMap.get(dto.documentId);
    if (locationAr?.name) {
      dto.name = locationAr.name;
    }
    if (locationAr?.addressLine1) {
      dto.addressLine1 = locationAr.addressLine1;
    }
    if (locationAr?.addressLine2) {
      dto.addressLine2 = locationAr.addressLine2;
    }

    const cityDocumentId = dto.city?.documentId;
    const cityAr = cityDocumentId ? cityArMap.get(cityDocumentId) : undefined;
    if (dto.city && cityAr?.name) {
      dto.city.name = cityAr.name;
    }
  });
}

/**
 * Walk a list of factory DTOs and patch location Arabic translations in place.
 * Cities now have `localizations` populated via the factory query, so only
 * location AR names need to be fetched separately.
 *
 * No-op when the app language is not Arabic.
 */
async function patchFactoryDtosWithArNames(
  http: ReturnType<typeof getHttpClient>,
  dtos: any[],
  lang: string
): Promise<void> {
  if (lang !== 'ar' || !Array.isArray(dtos) || dtos.length === 0) return;

  const locationIds: string[] = [];
  for (const dto of dtos) {
    if (typeof dto?.location?.documentId === 'string') {
      locationIds.push(dto.location.documentId);
    }
  }
  if (locationIds.length === 0) return;

  const locationArMap = await fetchLocationArEntriesMap(http, locationIds);

  for (const dto of dtos) {
    if (dto?.location?.documentId) {
      const arLocation = locationArMap.get(dto.location.documentId);
      if (arLocation?.name) {
        dto.location.name = arLocation.name;
      }
      if (arLocation?.addressLine1) {
        dto.location.addressLine1 = arLocation.addressLine1;
      }
      if (arLocation?.addressLine2) {
        dto.location.addressLine2 = arLocation.addressLine2;
      }
    }
  }
}

// ============================================================================
// Factories API
// ============================================================================

export const factoriesApi = {
  /**
   * Get all factories with optional filtering
   */
  async getFactories(filter?: FactoriesFilter): Promise<FactoryListItem[]> {
    const http = getHttpClient();
    const lang = resolveFactoriesLanguage();
    // NOTE: do NOT forward `locale` to the factory list. Strapi v5 propagates
    // the request locale into populated i18n sub-relations and DROPS items
    // that don't have a translation in that locale (cities/locations without
    // an AR version disappear). The frontend resolves the right locale by
    // reading the `localizations` sub-relation populated below.
    const queryParams = buildFactoryQuery({
      status: filter?.status === 'all' ? undefined : filter?.status,
      search: filter?.search,
    });
    const queryString = buildStrapiQueryString(queryParams);
    const url = `${STRAPI_ENDPOINTS.FACTORIES}${queryString}`;

    const [response, arMap] = await Promise.all([
      http.get<FactoryListResponse>(url),
      fetchFactoryArNamesMap(http),
    ]);
    // Patch nested city names to AR (in place on the raw DTOs) before
    // transforming to list items, so the joined "cities" string and the
    // `cities[]` array on each FactoryListItem render in Arabic.
    await patchFactoryDtosWithArNames(http, response.data.data, lang);
    return response.data.data
      .map(transformFactoryDtoToListItem)
      .map((f) => mergeArIntoFactoryListItem(f, arMap, lang));
  },

  /**
   * Get paginated factories with filtering
   */
  async getFactoriesPaginated(
    filter?: FactoriesFilter
  ): Promise<PaginatedFactories> {
    const http = getHttpClient();
    const lang = resolveFactoriesLanguage();
    const page = filter?.page ?? 1;
    const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE;

    // See note in getFactories — do NOT forward `locale` here.
    const queryParams = buildFactoryQuery({
      status: filter?.status === 'all' ? undefined : filter?.status,
      search: filter?.search,
      page,
      pageSize,
    });
    const queryString = buildStrapiQueryString(queryParams);
    const url = `${STRAPI_ENDPOINTS.FACTORIES}${queryString}`;

    const [response, arMap] = await Promise.all([
      http.get<FactoryListResponse>(url),
      fetchFactoryArNamesMap(http),
    ]);
    await patchFactoryDtosWithArNames(http, response.data.data, lang);
    const factories = response.data.data
      .map(transformFactoryDtoToListItem)
      .map((f) => mergeArIntoFactoryListItem(f, arMap, lang));

    // Extract pagination from response
    const meta = (
      response.data as unknown as {
        meta?: { pagination?: Record<string, unknown> };
      }
    )?.meta?.pagination;

    const pagination: PaginationMeta = {
      page: (meta?.page as number) ?? page,
      pageSize: (meta?.pageSize as number) ?? pageSize,
      pageCount: (meta?.pageCount as number) ?? 1,
      total: (meta?.total as number) ?? factories.length,
    };

    return { data: factories, pagination };
  },

  /**
   * Get single factory by documentId.
   * Fetches EN locale (with relations) and AR locale in parallel so Arabic
   * fields (name.ar, description.ar) are always populated for edit forms.
   */
  async getFactory(documentId: string): Promise<Factory | null> {
    const http = getHttpClient();
    const lang = resolveFactoriesLanguage();
    // Fetch the factory in the default locale (en). Cities have their
    // localizations populated so both EN/AR names are available. Locations
    // that lack an Arabic entry are handled via AR shim below.
    const queryParams = buildFactoryQuery({
      populate: ['location', 'cities'],
    });
    const queryString = buildStrapiQueryString(queryParams);
    const url = `${STRAPI_ENDPOINTS.FACTORY_BY_ID(documentId)}${queryString}`;
    const arQueryString = buildStrapiQueryString({
      locale: 'ar',
      'fields[0]': 'name',
      'fields[1]': 'description',
    });
    const arUrl = `${STRAPI_ENDPOINTS.FACTORY_BY_ID(
      documentId
    )}${arQueryString}`;

    try {
      const [enResult, arResult] = await Promise.allSettled([
        http.get<FactoryResponse>(url),
        http.get<FactoryResponse>(arUrl),
      ]);

      if (enResult.status === 'rejected') {
        const axiosError = enResult.reason as {
          response?: { status?: number };
        };
        if (axiosError.response?.status === 404) return null;
        throw enResult.reason;
      }

      const factory = transformFactoryDtoToFactory(enResult.value.data.data);

      if (arResult.status === 'fulfilled') {
        const arDto = arResult.value.data.data;
        if (arDto) {
          if (arDto.name?.trim()) {
            factory.name = { ...factory.name, ar: arDto.name };
          }
          if (arDto.description?.trim()) {
            factory.description = {
              ...(factory.description ?? { en: '' }),
              ar: arDto.description,
            };
          }
        }
      }

      // AR shim for location (Arabic UI only).
      if (lang === 'ar') {
        const locationDocumentId = factory.location?.documentId;

        const locationArMap = locationDocumentId
          ? await fetchLocationArEntriesMap(http, [locationDocumentId])
          : new Map<string, LocationArEntry>();

        const locationAr = locationDocumentId
          ? locationArMap.get(locationDocumentId)
          : undefined;
        if (factory.location && locationAr) {
          factory.location = {
            ...factory.location,
            name: locationAr.name || factory.location.name,
            addressLine1:
              locationAr.addressLine1 ?? factory.location.addressLine1,
            addressLine2:
              locationAr.addressLine2 ?? factory.location.addressLine2,
          };
        }
      }

      return factory;
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) return null;
      throw error;
    }
  },

  /**
   * Get factory statistics
   */
  async getFactoryStats(): Promise<FactoryStats> {
    const factories = await factoriesApi.getFactories();
    return calculateFactoryStats(factories);
  },

  /**
   * Create a new factory with i18n support
   * 1. Creates in English (default locale)
   * 2. Creates Arabic localization if Arabic name/description provided
   */
  async createFactory(data: CreateFactoryRequest): Promise<Factory> {
    const http = getHttpClient();
    const requestData = transformCreateRequestToDto(data);

    // Step 1: Create in English locale
    const response = await http.post<FactoryResponse>(
      `${STRAPI_ENDPOINTS.FACTORIES}?locale=en`,
      { data: requestData }
    );

    const documentId = response.data.data.documentId;

    // Step 2: Create Arabic localization if Arabic content provided
    if (data.name.ar || data.description?.ar) {
      await http.put<FactoryResponse>(
        `${STRAPI_ENDPOINTS.FACTORY_BY_ID(documentId)}?locale=ar`,
        {
          data: {
            name: data.name.ar || data.name.en,
            description: data.description?.ar || data.description?.en,
          },
        }
      );
    }

    // Fetch the full factory with populated relations
    const created = await factoriesApi.getFactory(documentId);
    return created!;
  },

  /**
   * Update an existing factory with i18n support
   * 1. Updates English locale
   * 2. Updates Arabic localization if Arabic content provided
   */
  async updateFactory(
    documentId: string,
    data: UpdateFactoryRequest
  ): Promise<Factory> {
    const http = getHttpClient();
    const updateData = transformUpdateRequestToDto(data);

    // Step 1: Update English locale
    await http.put<FactoryResponse>(
      `${STRAPI_ENDPOINTS.FACTORY_BY_ID(documentId)}?locale=en`,
      { data: updateData }
    );

    // Step 2: Update Arabic localization if Arabic content provided
    if (data.name?.ar || data.description?.ar) {
      await http.put<FactoryResponse>(
        `${STRAPI_ENDPOINTS.FACTORY_BY_ID(documentId)}?locale=ar`,
        {
          data: {
            name: data.name?.ar || data.name?.en,
            description: data.description?.ar || data.description?.en,
          },
        }
      );
    }

    // Fetch the updated factory with populated relations
    const updated = await factoriesApi.getFactory(documentId);
    return updated!;
  },

  /**
   * Soft delete a factory
   */
  async deleteFactory(
    documentId: string,
    deletedBy?: string
  ): Promise<boolean> {
    const http = getHttpClient();

    try {
      await http.put<FactoryResponse>(
        STRAPI_ENDPOINTS.FACTORY_BY_ID(documentId),
        {
          data: {
            deletedAt: new Date().toISOString(),
            deletedBy: deletedBy ?? 'system',
            factoryStatus: 'inactive',
          },
        }
      );
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Hard delete a factory (permanent)
   */
  async hardDeleteFactory(documentId: string): Promise<boolean> {
    const http = getHttpClient();

    try {
      await http.delete(STRAPI_ENDPOINTS.FACTORY_BY_ID(documentId));
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Search factories by query
   */
  async searchFactories(query: string): Promise<FactoryListItem[]> {
    return factoriesApi.getFactories({ search: query });
  },

  /**
   * Get factories by country
   */
  async getFactoriesByCountry(countryCode: string): Promise<FactoryListItem[]> {
    return factoriesApi.getFactories({ countryCode });
  },

  /**
   * Get factories that serve a specific city
   * Uses the cities many-to-many relation on factory
   */
  async getFactoriesByCity(cityDocumentId: string): Promise<FactoryListItem[]> {
    const http = getHttpClient();
    // Use explicit field population to avoid Strapi v5 depth limits
    // (using * would try to populate city.region which exceeds 2-level max)
    const queryString = buildStrapiQueryString({
      'populate[location][fields][0]': 'id',
      'populate[location][fields][1]': 'documentId',
      'populate[location][fields][2]': 'name',
      'populate[location][fields][3]': 'addressLine1',
      'populate[location][populate][city][fields][0]': 'id',
      'populate[location][populate][city][fields][1]': 'name',
      'populate[location][populate][country][fields][0]': 'id',
      'populate[location][populate][country][fields][1]': 'name',
      'populate[location][populate][country][fields][2]': 'code',
      'populate[cities][fields][0]': 'id',
      'populate[cities][fields][1]': 'documentId',
      'populate[cities][fields][2]': 'code',
      'populate[cities][fields][3]': 'name',
      'filters[factoryStatus][$eq]': 'active',
      'filters[cities][documentId][$eq]': cityDocumentId,
      locale: 'en',
    });
    const url = `${STRAPI_ENDPOINTS.FACTORIES}${queryString}`;

    const response = await http.get<FactoryListResponse>(url);
    return response.data.data.map(transformFactoryDtoToListItem);
  },
};

// ============================================================================
// Cities API (reuse from addressbook pattern)
// ============================================================================

export const citiesApi = {
  /**
   * Get cities with pagination support
   */
  async getAll(
    page = 1,
    pageSize = 25
  ): Promise<{ data: City[]; hasMore: boolean }> {
    const http = getHttpClient();
    const lang = resolveFactoriesLanguage();
    const queryString = buildStrapiQueryString({
      'populate[country]': '*',
      'filters[isActive][$eq]': 'true',
      sort: 'name:asc',
      locale: 'en',
      'pagination[page]': String(page),
      'pagination[pageSize]': String(pageSize),
    });
    const url = `${STRAPI_ENDPOINTS.CITIES}${queryString}`;

    const response = await http.get<{
      data: CityDto[];
      meta?: { pagination?: { pageCount?: number } };
    }>(url);
    await patchCityDtosWithArNames(http, response.data.data, lang);
    const pageCount = response.data.meta?.pagination?.pageCount ?? 1;
    return {
      data: response.data.data.map(transformCityDtoToCity),
      hasMore: page < pageCount,
    };
  },

  /**
   * Get cities by country code
   */
  async getByCountry(countryCode: string): Promise<City[]> {
    const http = getHttpClient();
    const lang = resolveFactoriesLanguage();
    const queryString = buildStrapiQueryString({
      'populate[country]': '*',
      'filters[isActive][$eq]': 'true',
      'filters[country][code][$eq]': countryCode,
      sort: 'name:asc',
      locale: 'en',
    });
    const url = `${STRAPI_ENDPOINTS.CITIES}${queryString}`;

    const response = await http.get<{ data: CityDto[] }>(url);
    await patchCityDtosWithArNames(http, response.data.data, lang);
    return response.data.data.map(transformCityDtoToCity);
  },

  /**
   * Search cities by name or code
   */
  async getBySearch(query: string, pageSize = 25): Promise<City[]> {
    const http = getHttpClient();
    const lang = resolveFactoriesLanguage();
    const queryString = buildStrapiQueryString({
      'populate[country]': '*',
      'filters[isActive][$eq]': 'true',
      'filters[$or][0][name][$containsi]': query,
      'filters[$or][1][code][$containsi]': query,
      sort: 'name:asc',
      locale: 'en',
      'pagination[pageSize]': String(pageSize),
    });
    const url = `${STRAPI_ENDPOINTS.CITIES}${queryString}`;

    const response = await http.get<{ data: CityDto[] }>(url);
    await patchCityDtosWithArNames(http, response.data.data, lang);
    return response.data.data.map(transformCityDtoToCity);
  },

};

// ============================================================================
// Countries API
// ============================================================================

export const countriesApi = {
  /**
   * Get all countries
   */
  async getAll(): Promise<Country[]> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': 'true',
      sort: 'name:asc',
    });
    const url = `${STRAPI_ENDPOINTS.COUNTRIES}${queryString}`;

    const response = await http.get<{ data: CountryDto[] }>(url);
    return response.data.data.map(transformCountryDtoToCountry);
  },
};

// ============================================================================
// Locations API (from addressbook)
// ============================================================================

export const locationsApi = {
  /**
   * Get all active locations
   */
  async getAll(): Promise<Location[]> {
    const http = getHttpClient();
    const lang = resolveFactoriesLanguage();
    const queryString = buildStrapiQueryString({
      'populate[city][populate][country]': '*',
      'populate[country]': '*',
      'filters[status][$eq]': 'active',
      sort: 'name:asc',
      locale: 'en',
    });
    const url = `${STRAPI_ENDPOINTS.LOCATIONS}${queryString}`;

    const response = await http.get<{ data: LocationDto[] }>(url);
    await patchLocationDtosWithArNames(http, response.data.data, lang);
    return response.data.data.map(transformLocationDtoToLocation);
  },

  /**
   * Get locations by country code
   */
  async getByCountry(countryCode: string): Promise<Location[]> {
    const http = getHttpClient();
    const lang = resolveFactoriesLanguage();
    const queryString = buildStrapiQueryString({
      'populate[city][populate][country]': '*',
      'populate[country]': '*',
      'filters[status][$eq]': 'active',
      'filters[country][code][$eq]': countryCode,
      sort: 'name:asc',
      locale: 'en',
    });
    const url = `${STRAPI_ENDPOINTS.LOCATIONS}${queryString}`;

    const response = await http.get<{ data: LocationDto[] }>(url);
    await patchLocationDtosWithArNames(http, response.data.data, lang);
    return response.data.data.map(transformLocationDtoToLocation);
  },
};
